import type { schemaType } from '~~/modules/api/rurality/v3/search/cottages/schema'
import type { MetaSuccessRuralitySchemaType } from '~~/modules/api/utils/rurality/v3/fetch/ruralitySchema'
import type { schemaType as FacetsSchemaType } from '~~/modules/api/rurality/v3/search/seo/facet/schema'
import { useCottageListFilters } from '~~/modules/views/cottage-list/composables/useCottageListFilters'
import type { CottageListFilters } from '~~/modules/views/cottage-list/composables/useCottageListFilters'

export interface CottageListResultsOptions {
  cottagesData: schemaType
  cottagesMeta: MetaSuccessRuralitySchemaType
  locationName: string
  facetsData?: FacetsSchemaType
}

export interface CottageListResultsReturn {
  currentPage: Ref<number>
  sortValue: Ref<string>
  sortOptions: Array<{ value: string; label: string }>
  formattedTotalCottages: ComputedRef<string>
  formattedNearCottagesTotal: ComputedRef<string>
  currentFiltersData: ComputedRef<CottageListFilters>
  hasActiveFilters: ComputedRef<boolean>
  paginationText: ComputedRef<string>
  showFilters: Ref<boolean>
  showPageH1: ComputedRef<boolean>
  showSortSelect: ComputedRef<boolean>
  pageH1Heading: ComputedRef<string>
  showNearCottagesAlert: ComputedRef<boolean>
  showNearCottagesHeading: ComputedRef<boolean>
  handleLocationChange: (location: string) => void
  handleOnlineBookingFilter: () => void
  handleExactCapacityFilter: () => void
  handleRemoveFilter: (filterId: number) => void
  handleFiltersSubmit: (filters: CottageListFilters) => void
  toggleFilters: () => void
  toggleMap: () => void
  handlePageFilter: (page: number) => void
}

export function useCottageListResults(
  options: CottageListResultsOptions,
): CottageListResultsReturn {
  const route = useRoute()

  if (route.query.viz === 'map') {
    const query = { ...route.query }
    delete query.viz
    navigateTo(
      {
        path: routes().cottageListMap(),
        query,
      },
      {
        external: true,
      },
    )
  }

  const {
    parseUrlParams,
    handleLocationChange: handleLocationChangeBase,
    handleOnlineBookingFilter,
    handleExactCapacityFilter,
    handleRemoveFilter,
    updateFilters,
    getCurrentFilters,
    handlePageFilter,
  } = useCottageListFilters()

  const itemsPerPage = computed(
    () => options.cottagesMeta.pagination.maxPerPage,
  )
  const currentPage = ref(1)
  const showFilters = ref(false)
  const sortValue = ref('relevant')

  const initialParams = parseUrlParams()

  if (initialParams.page) {
    currentPage.value = initialParams.page
  }
  if (initialParams.sort) {
    sortValue.value = initialParams.sort
  }

  const sortOptions = [
    { value: 'relevant', label: t('views.cottage-list.results.sort.relevant') },
    { value: 'updated', label: t('views.cottage-list.results.sort.updated') },
    {
      value: 'numreviews',
      label: t('views.cottage-list.results.sort.numreviews'),
    },
    {
      value: 'averagereviews',
      label: t('views.cottage-list.results.sort.averagereviews'),
    },
    {
      value: 'price_asc',
      label: t('views.cottage-list.results.sort.priceAsc'),
    },
  ]

  const formattedTotalCottages = computed(() =>
    toFormattedNumber(options.cottagesData.totalCottages),
  )

  const formattedNearCottagesTotal = computed(() =>
    toFormattedNumber(options.cottagesData.nearCottagesTotal),
  )

  const currentFiltersData = computed(() => getCurrentFilters())

  const hasActiveFilters = computed(() => {
    const filters = currentFiltersData.value
    return !!(
      filters.f ||
      filters.minPrice !== undefined ||
      filters.maxPrice !== undefined ||
      filters.dateIn ||
      filters.dateOut ||
      filters.c !== undefined ||
      filters.ec !== undefined ||
      filters.r ||
      filters.bo !== undefined ||
      filters.q ||
      filters.fc !== undefined ||
      filters.of !== undefined
    )
  })

  const paginationText = computed(() => {
    const total = options.cottagesMeta.totalItems || 0
    if (total === 0) return ''

    const start = (currentPage.value - 1) * itemsPerPage.value + 1
    const end = Math.min(currentPage.value * itemsPerPage.value, total)

    return t('views.cottage-list.results.pagination.text', {
      start: toFormattedNumber(start),
      end: toFormattedNumber(end),
      total: toFormattedNumber(total),
    })
  })

  const isCornerPage = computed(() => {
    return !!options.cottagesData.corner
  })

  const showPageH1 = computed(() => {
    const hasCottages = parseInt(formattedTotalCottages.value) > 0
    return isCornerPage.value || hasCottages
  })

  const showSortSelect = computed(() => {
    return parseInt(formattedTotalCottages.value) > 0
  })

  const pageH1Heading = computed(() => {
    if (options.facetsData?.caption) {
      return `${options.facetsData.caption} (${formattedTotalCottages.value})`
    }
    if (isCornerPage.value && options.cottagesData.destination) {
      return t('views.cottage-list.results.heading.cornerCottages', {
        n: formattedNearCottagesTotal.value,
        c: options.cottagesData.corner!.name,
        l: options.cottagesData.destination.name,
      })
    }
    if (options.locationName && !hasActiveFilters.value) {
      return t('views.cottage-list.results.heading.allCottages', {
        n: formattedTotalCottages.value,
        l: options.locationName,
      })
    }
    return t('views.cottage-list.results.heading.filteredCottages', {
      n: formattedTotalCottages.value,
    })
  })

  const showNearCottagesAlert = computed(() => {
    if (isCornerPage.value) {
      return false
    }

    const hasNearCottages = !!options.cottagesData.nearCottagesTotal

    return hasNearCottages
  })

  const showNearCottagesHeading = computed(() => {
    if (isCornerPage.value) {
      return false
    }

    const hasNearCottages = !!options.cottagesData.nearCottagesTotal
    return hasNearCottages
  })

  function handleFiltersSubmit(filters: CottageListFilters) {
    showFilters.value = false
    updateFilters(
      { ...filters, sort: sortValue.value || undefined },
      1,
      options.cottagesData,
    )
  }

  function toggleFilters() {
    showFilters.value = !showFilters.value
  }

  function toggleMap() {
    const query = { ...route.query }

    navigateTo(
      {
        path: routes().cottageListMap(),
        query,
      },
      {
        external: true,
      },
    )
  }

  function handleLocationChange(location: string) {
    handleLocationChangeBase(location, options.cottagesData)
  }

  function handlePageFilterWithData(page: number) {
    handlePageFilter(page, options.cottagesData)
  }

  watch(currentPage, () => {
    const currentFilters = getCurrentFilters()
    updateFilters(currentFilters, currentPage.value, options.cottagesData)
  })

  watch(sortValue, newSort => {
    const currentFilters = getCurrentFilters()
    updateFilters(
      {
        ...currentFilters,
        sort: newSort === 'relevant' ? undefined : newSort,
      },
      1,
      options.cottagesData,
    )
  })

  return {
    currentPage,
    sortValue,
    sortOptions,
    formattedTotalCottages,
    formattedNearCottagesTotal,
    currentFiltersData,
    hasActiveFilters,
    paginationText,
    showFilters,
    showPageH1,
    showSortSelect,
    pageH1Heading,
    showNearCottagesAlert,
    showNearCottagesHeading,
    handleLocationChange,
    handleOnlineBookingFilter,
    handleExactCapacityFilter,
    handleRemoveFilter,
    handleFiltersSubmit,
    toggleFilters,
    toggleMap,
    handlePageFilter: handlePageFilterWithData,
  }
}
